import {
  type Except,
  isEmpty,
  type SetOptional,
  type ValidatedKey,
} from '@festina-life/core'
import { useField } from 'formik'
import type { ComponentProps, ReactNode } from 'react'

import { Select } from '../../forms'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
  handleOnBlur,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useSimpleValueDifferenceCheck } from '../formikUtils/useSimpleValueDifferenceCheck'
import { withFormikInputDiffing } from '../formikUtils/withFormikInputDiffing'
import type { FormikInputPropsForWrapper } from '../types'

type Props<T> = SetOptional<
  Except<
    ComponentProps<typeof Select<T>>,
    'onChange' | 'id' | 'onBlur' | 'wrapperProps'
  >,
  'sort'
> & {
  name: ValidatedKey
  defaultValue?: T
  onBlur?: (value: T | undefined) => void
} & Except<FormikInputPropsForWrapper, 'validationText'>

const FormikSelectInternal = <T,>({
  name,
  options,
  description,
  label,
  defaultValue,
  onDefaultValueClick,
  isRequired,
  disabled,
  onBlur,
  hasRightColumnAlways,
  existingValueConfig,
  className,
  ...restProps
}: Props<T>) => {
  const [field, meta, { setValue, setTouched }] = useField(name)
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)

  const existingValue = useExistingValuesContext()?.getExistingValue?.(name) as
    | T
    | undefined

  const toRenderString = (
    value: T | undefined,
    renderFn: ((item: T) => ReactNode) | undefined,
  ) => {
    if (isEmpty(value)) {
      return value
    }

    return renderFn ? renderFn(value) : `${value}`
  }
  const renderDefault = toRenderString(defaultValue, restProps.renderFn)
  const renderExistingValue = toRenderString(
    existingValue as T, // form values and ExistingValues have the same generic type T. In both places the name property is used to get the correct value. Therefore it is safe to cast the current value to T.
    restProps.renderFn,
  )

  const inputValueDifferenceFromExistingValue =
    useSimpleValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    existingValue: renderExistingValue,
    inputValueDifferenceFromExistingValue,
    label,
    existingValueConfig,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      defaultValue: renderDefault,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick,
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      error,
      hasRightColumnAlways,
      className,
    },
  })

  return (
    <Select
      wrapperProps={wrapperProps}
      id={name}
      isInError={isInError}
      options={options}
      {...field}
      {...restProps}
      disabled={isFieldDisabled}
      onChange={async (v) => {
        await setTouched(true)
        await setValue(v)
      }}
      onBlurValue={(value) => {
        onBlur?.(value)
      }}
      onBlur={(event) => handleOnBlur({ event, field })}
    />
  )
}

export const FormikSelect = withFormikInputDiffing(FormikSelectInternal)
